import { Button, Form } from 'components';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import countries from 'constants/countries';
import { useUpdateLocationAddress } from 'api/locations/hooks';
import validationSchemas from './schemas';

const getAddressPayload = ({ street, apartment, city, zip, state, country }) => ({
  line1: street,
  line2: apartment,
  city,
  postal_code: zip,
  state,
  country,
});

const LocationAddressForm = ({ type, initialValues, onNext, onPrevious }) => {
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'locations' });
  const { mutate: updateLocationAddress, isPending } = useUpdateLocationAddress();

  const formInitialValues = useMemo(
    () => ({
      uid: initialValues?.uid || undefined,
      street: initialValues?.street || '',
      apartment: initialValues?.apartment || '',
      city: initialValues?.city || '',
      zip: initialValues?.zip || '',
      state: initialValues?.state || '',
      country: initialValues?.country || '',
    }),
    [initialValues],
  );
  const validationSchema = useMemo(() => validationSchemas.location_address, []);

  const handleSubmit = useCallback(
    (formData) => {
      const { uid, ...formDataWithoutUid } = formData;
      if (type === 'update') {
        updateLocationAddress(
          { uid, address: getAddressPayload(formDataWithoutUid) },
          {
            onSuccess: () => {
              if (onNext) onNext();
            },
          },
        );
      } else if (onNext) {
        onNext();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateLocationAddress, onNext, type],
  );

  return (
    <Form
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      renderDefaultButtons={false}
    >
      <Form.GroupHorizontal label={t('onboarding.streetLabel')} required>
        <Form.Input name="street" />
      </Form.GroupHorizontal>
      <Form.GroupHorizontal label={t('onboarding.apartmentLabel')}>
        <Form.Input name="apartment" />
      </Form.GroupHorizontal>
      <HorizontalWrapper>
        <Form.GroupHorizontal className="w-50" label={t('onboarding.cityLabel')} required>
          <Form.Input name="city" />
        </Form.GroupHorizontal>
        <Form.GroupHorizontal className="w-50" label={t('onboarding.zipLabel')} required>
          <Form.Input name="zip" />
        </Form.GroupHorizontal>
      </HorizontalWrapper>
      <HorizontalWrapper>
        <Form.GroupHorizontal className="w-50" label={t('onboarding.stateLabel')} required>
          <Form.Input name="state" />
        </Form.GroupHorizontal>
        <Form.GroupHorizontal className="w-50" label={t('onboarding.countryLabel')} required>
          <Form.MultiSelect
            isMulti={false}
            options={countries.map((country) => ({
              value: country.code,
              label: country.name,
            }))}
            name="country"
            required
          />
        </Form.GroupHorizontal>
      </HorizontalWrapper>

      <ButtonsSection>
        {onPrevious && (
          <Button variant="secondary" block onClick={onPrevious}>
            {t('onboarding.previousStep')}
          </Button>
        )}
        <Form.Submit
          className="text-uppercase mt-0"
          size="lg"
          variant="primary"
          block
          isLoading={isPending}
          ignoreClickedState
        >
          {t('onboarding.nextStep')}
        </Form.Submit>
      </ButtonsSection>
    </Form>
  );
};

const ButtonsSection = styled.div`
  width: 100%;
  margin-top: 48px;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
`;

const HorizontalWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;

  > .w-50 {
    flex: 1 1 0;
    width: auto !important;
    min-width: 0;
  }
`;

LocationAddressForm.propTypes = {
  type: PropTypes.oneOf(['update']),
  initialValues: PropTypes.shape({
    uid: PropTypes.string,
    street: PropTypes.string,
    apartment: PropTypes.string,
    city: PropTypes.string,
    zip: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
  }),
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
};

LocationAddressForm.defaultProps = {
  type: undefined,
  initialValues: undefined,
  onNext: undefined,
  onPrevious: undefined,
};

export default LocationAddressForm;
